<template>
	<v-container id="login" fluid>
		<v-row justify="center">
			<v-col sm="12" md="6" lg="5">
				<v-img :src="image.splash" max-height="225" contain />
			</v-col>
		</v-row>

		<layout-error-boundary
			v-slot="{ hasError, resetError }"
			:on-error="handleFormError"
			stop-propagation
		>
			<v-form v-model="valid" @submit.prevent="submit">
				<v-row justify="center">
					<v-col cols="11" sm="9" md="5" lg="4">
						<v-alert
							v-if="hasError"
							type="error"
							border="left"
							dismissible
							outlined
							@click.native="resetError"
						>
							<span class="body-2 error--text">{{ errMessage }}</span>
						</v-alert>

						<v-text-field
							v-model="email"
							:rules="emailRules"
							:disabled="loading"
							label="Email"
							prepend-icon="fa-user"
							type="text"
							required
							autocapitalize="off"
							autocorrect="off"
						/>

						<v-text-field
							v-model="password"
							:rules="passwordRules"
							:disabled="loading"
							label="Password"
							prepend-icon="fa-lock"
							type="password"
							required
							autocapitalize="off"
							autocorrect="off"
						/>

						<v-checkbox
							v-if="showRememberMe"
							v-model="rememberMe"
							:disabled="loading"
							label="Save Credentials"
						/>
					</v-col>
				</v-row>

				<v-row justify="center">
					<v-col sm="12" md="6" lg="5" align="center">
						<v-btn
							class="submit"
							:loading="loading"
							:disabled="!valid || loading"
							color="rust-red"
							min-width="150"
							type="submit"
							large
						>
							Log In
						</v-btn>
					</v-col>
				</v-row>
			</v-form>

			<v-footer v-if="showBioAuth" id="footer" fixed padless light>
				<div class="wrap">
					<v-btn
						v-blur
						:loading="bioAuthLoading"
						class="bioauth-btn"
						x-large
						absolute
						fab
						top
						@click="bioAuthenticate"
					>
						<v-img
							class="image-fingerprint"
							contain
							width="40"
							:src="image.fingerprint"
						/>
					</v-btn>
					<p class="body-1 font-weight-medium">Use Fingerprint</p>
				</div>
			</v-footer>
		</layout-error-boundary>
	</v-container>
</template>

<style lang="scss" scoped>
#login {
	// Footer height + (height of fab button / 2)
	margin-bottom: 108px;
}

#footer {
	text-align: center;
	border-color: var(--pale-grey-base);

	.wrap {
		position: relative;
		width: 100%;
		height: 100%;
	}

	.bioauth-btn {
		width: 86px;
		height: 86px;

		left: 50%;
		margin-left: -43px;

		.image-fingerprint {
			max-width: 60%;
		}
	}

	p {
		position: relative;
		z-index: 5;
		margin: 22px 0 0 0;
		padding: 12px 0 6px 0;
		color: rgba(0, 0, 0, 0.66);
	}
}

#footer,
#footer .bioauth-btn {
	background-color: var(--v-pale-grey-base);
	box-shadow: inset 0px 11px 15px -7px rgba(0, 0, 0, 0.2);
}

#footer .bioauth-btn {
	box-shadow: inset 0px 11px 12px -5px rgba(0, 0, 0, 0.4);
}
</style>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { Storage } from "@/utils/storage";
import strings from "@/utils/strings";
import { ForbiddenError } from "@/services/ability";

const storage = Storage.local();

export default {
	data: () => ({
		strings: strings.login,
		valid: true,
		loading: false,
		bioAuthLoading: false,
		errMessage: null,

		email: null,
		emailRules: [
			v => !!v || "Email is required",
			v => /.+@.+/.test(v) || "Email must be valid",
		],

		password: null,
		passwordRules: [v => !!v || "Password is required"],

		image: {
			splash: require("@/assets/login-splash.svg"),
			fingerprint: require("@/assets/fingerprint.svg"),
		},
	}),

	computed: {
		...mapGetters("config", ["inMobileApp"]),
		...mapState("auth", ["biometrics"]),

		rememberMe: {
			get() {
				return this.biometrics.optedIn;
			},
			set(value) {
				return this.$store.commit("auth/setBiometrics", {
					optedIn: value,
				});
			},
		},

		showRememberMe() {
			return this.inMobileApp;
		},

		showBioAuth() {
			return this.biometrics.available && this.biometrics.stored;
		},
	},

	mounted() {
		const biometrics = storage.get("biometrics");
		if (biometrics) {
			this.$log(`Login page: biometrics: ${JSON.stringify(biometrics)}`);
		}
	},

	methods: {
		...mapActions({
			signIn: "auth/signIn",
			signInBio: "auth/signInBio",
			storeCredentials: "auth/storeCredentials",
		}),

		async submit() {
			const credentials = {
				email: this.email.trim(),
				password: this.password,
			};

			this.loading = true;
			try {
				await this.signIn(credentials);
			} finally {
				this.loading = false;
			}

			// User just checked 'rememberMe' and this is their first time signing in
			if (this.rememberMe && !this.credentialsStored) {
				await this.storeCredentials(credentials);
			}

			this.redirect();
		},

		async bioAuthenticate() {
			this.bioAuthLoading = true;

			try {
				await this.signInBio();
				this.redirect();
			} finally {
				this.bioAuthLoading = false;
			}
		},

		async handleFormError(err, vm, info) {
			this.$log(`Error submitting login form: ${err}`);
			this.errMessage =
				err instanceof ForbiddenError
					? this.strings.forbiddenError
					: this.strings.defaultError;
		},

		redirect() {
			// Redirect to the originally requested page, or to the home page
			return this.$router.push(
				this.$route.query.redirectFrom || { name: "Home" }
			);
		},
	},
};
</script>
